import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { CallistoLogo } from "../components/CallistoLogo";
import { Loader2, Eye, EyeOff, ShieldCheck, Zap, MapPinned } from "lucide-react";
import { toast } from "sonner";
import { currentMonthIST } from "../lib/datetime";

const BRAND_PANEL_BG =
  "/login-brand-panel.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const from = location.state?.from?.pathname || "/dashboard";

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login({ email, password, remember_me: remember });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome back, ${res.user.full_name.split(" ")[0]}`);
    navigate(from, { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Brand panel */}
      <div
        className="relative md:w-1/2 lg:w-[55%] callisto-brand-panel text-white flex flex-col justify-between p-8 md:p-12 min-h-[260px] md:min-h-screen"
      >
        <div
          className="absolute inset-0 callisto-brand-grid opacity-50 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `url(${BRAND_PANEL_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "screen",
          }}
          aria-hidden
        />
        <div className="relative z-10">
          <CallistoLogo variant="dark" size="md" />
        </div>
        <div className="relative z-10 max-w-md hidden md:block">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.25em] text-callisto-gold mb-4">
            Field-first SFA Platform
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight text-white">
            One workspace for every Callisto field rep, manager, and director.
          </h2>
          <p className="mt-4 text-white/70 leading-relaxed">
            Plan routes, log doctor visits, track attendance, file expenses, and
            roll up the entire team's performance in real time.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { icon: ShieldCheck, label: "Secure" },
              { icon: Zap, label: "Real-time" },
              { icon: MapPinned, label: "Field-ready" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="rounded-lg bg-white/5 border border-white/10 px-3 py-3 flex flex-col items-center gap-1.5"
              >
                <Icon className="h-4 w-4 text-callisto-gold" />
                <span className="text-[11px] text-white/70 uppercase tracking-wider">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <p className="relative z-10 hidden md:block text-xs text-white/40">
          © {currentMonthIST().year} Callisto Pharmaceuticals Pvt. Ltd.
        </p>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-callisto-gold">
              Welcome back
            </span>
            <h1 className="mt-2 text-3xl md:text-4xl font-bold text-callisto-navy tracking-tight">
              Sign in to Callisto SFA
            </h1>
            <p className="mt-2 text-callisto-muted">
              Enter your work email to access the field workspace.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-callisto-navy font-medium">
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@callistosfa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="h-11 bg-white border-callisto focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-callisto-navy font-medium">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-callisto-gold hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11 pr-10 bg-white border-callisto focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-callisto-muted hover:text-callisto-navy"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={remember}
                onCheckedChange={(v) => setRemember(!!v)}
                data-testid="login-remember-checkbox"
              />
              <Label htmlFor="remember" className="text-sm text-callisto-body cursor-pointer">
                Remember me on this device
              </Label>
            </div>

            {error && (
              <div
                className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-callisto-muted text-center">
            Invite-only access. Need an account? Ask your admin to send
            you an invite.
          </p>
        </div>
      </div>
    </div>
  );
}
